import type { SitePrivateConfig, SitePublicConfig } from '@centric-os/types';
import { defineStore, type StateTree } from 'pinia';
import { useSiteConfigStore } from '../use-site-config-store';
import { useStationConfigStore } from '../use-station-config-store';
import type { Institution } from '@compassdigital/sdk.typescript/interface/mealplan';

/**
 * Interface representing the state of the `useMealPlanStore`.
 */
interface State extends StateTree {
  institutionList: Institution[];
}

/**
 * Pinia store for managing meal plan configurations.
 * This store provides actions to save both private and public meal plan configurations for a site or another entity (e.g., a station).
 *
 * @param {string} [storeId='mealPlan'] - The unique ID of the store. Defaults to 'mealPlan'.
 * @returns {import('pinia').Store} The Pinia store instance for managing meal plan configurations.
 */
export function useMealPlanStore(storeId = 'mealPlan') {
  const store = defineStore(storeId, {
    state: (): State => ({
      institutionList: [],
    }),
    actions: {
      /**
       * Saves both private and public meal plan configurations.
       * This method will either save the configurations for a site or another entity, such as a station,
       * depending on the `isSiteConfig` flag.
       *
       * @param {string} id - The ID of the entity (either a site or station) for which the configurations are being saved.
       * @param {boolean} isSiteConfig - Flag indicating whether the configurations are for a site (`true`) or for another entity like a station (`false`).
       * @param {Partial<SitePrivateConfig>} privatePayload - A partial object containing the private configuration data to be saved.
       * @param {Partial<SitePublicConfig>} publicPayload - A partial object containing the public configuration data to be saved.
       * @returns {Promise<void>} A promise that resolves once both private and public configurations are successfully saved.
       */
      async saveMealPlan(
        id: string,
        isSiteConfig: boolean,
        privatePayload: Partial<SitePrivateConfig>,
        publicPayload: Partial<SitePublicConfig>,
      ): Promise<void> {
        // Save private and public meal plan configurations for the site
        if (isSiteConfig) {
          // Access the site configurations store
          const siteConfigStore = useSiteConfigStore();

          // Save both private and public configurations concurrently
          await Promise.all([
            siteConfigStore.savePrivate(id, privatePayload),
            siteConfigStore.savePublic(id, publicPayload),
          ]);
        } else {
          const stationConfigStore = useStationConfigStore();

          // Save both private and public configurations concurrently
          await Promise.all([
            stationConfigStore.saveStationPrivateConfig(id, privatePayload),
            stationConfigStore.saveStationPublicConfig(id, publicPayload),
          ]);
        }
      },
      async getInstitutionList(): Promise<void> {
        const resp = await this.cdlApi.get('/mealplan/institutions');
        const institutionList: Institution[] =
          resp.data.institutions.InstitutionList.Institutions ?? [];
        this.institutionList = institutionList;
      },
    },
    pagination: false,
  });

  return store();
}
